/**
 * Situação de RH de uma pessoa, como pílula.
 *
 * Nasceu para as grades de Conjuntos (Samuel, 17/09/2026), que passaram a mostrar
 * se o motorista ou o operador está Ativo, de Férias, Afastado ou Demitido. A cor
 * responde à pergunta que a escala faz de longe: dá para contar com esta pessoa
 * hoje? Verde é sim; âmbar é ausência com volta prevista; vermelho é saída.
 *
 * O rótulo vem do backend, que é quem deriva a situação. O mapa daqui só escolhe o
 * tom, para o dia em que a origem publicar um estado novo: sem tom conhecido a
 * pílula sai neutra em vez de sumir.
 *
 * Desde as Ausências do DP (01/11/2026), `ausenciaAte` é o último dia ausente, e a
 * pílula de Férias ou Afastado diz no hover quando a pessoa volta. Sem a data não há
 * dica: quem é Férias só pelo ERP não tem período nenhum, e é assim que fica de fora.
 */
import Box from '@mui/material/Box'
import Tooltip from '@mui/material/Tooltip'
import { useI18n } from '@/core/i18n/useI18n'
import type { SituacaoColaborador } from '../../core/api/types'
import { fmtDate } from '../format'
import type { Tone } from '../status'
import { StatusChip } from './StatusChip'

const TOM: Record<SituacaoColaborador, Tone> = {
  ativo: 'success',
  ferias: 'warning',
  afastado: 'warning',
  demitido: 'error',
  bloqueado: 'neutral',
}

/** O dia seguinte ao último dia ausente, sem passar pelo fuso do navegador. */
function diaSeguinte(iso: string): string {
  const [ano, mes, dia] = iso.split('-').map(Number)
  return new Date(Date.UTC(ano, mes - 1, dia + 1)).toISOString().slice(0, 10)
}

export function SituacaoPessoa({ situacao, label, ausenciaAte }: {
  situacao?: SituacaoColaborador | null
  label?: string | null
  ausenciaAte?: string | null
}) {
  const { t } = useI18n()
  if (!situacao && !label) {
    return <Box component="span" sx={{ color: 'text.disabled' }}>—</Box>
  }
  const chip = <StatusChip label={label ?? situacao ?? ''} tone={(situacao && TOM[situacao]) || 'neutral'} />
  if (!ausenciaAte || (situacao !== 'ferias' && situacao !== 'afastado')) return chip
  return (
    <Tooltip title={t('situacaoPessoa.retornoPrevisto', { data: fmtDate(diaSeguinte(ausenciaAte)) })} describeChild>
      <Box component="span" sx={{ display: 'inline-flex' }}>{chip}</Box>
    </Tooltip>
  )
}
